import { createPortal } from "react-dom";
import { useEffect, useId, useRef, useState } from "react";
import { cn } from "../utils/cn";

// ── Scroll lock ───────────────────────────────────────────────────────────────
// Uses a body dataset counter so Modal and Drawer cooperate correctly when
// stacked (e.g. a Drawer opened on top of a Modal).

function lockBodyScroll() {
  const n = parseInt(document.body.dataset.scrollLocks ?? "0", 10);
  if (n === 0) {
    const bar = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    if (bar > 0) document.body.style.paddingRight = `${bar}px`;
  }
  document.body.dataset.scrollLocks = String(n + 1);
}

function unlockBodyScroll() {
  const n = Math.max(0, parseInt(document.body.dataset.scrollLocks ?? "0", 10) - 1);
  document.body.dataset.scrollLocks = String(n);
  if (n === 0) {
    document.body.style.overflow = "";
    document.body.style.paddingRight = "";
  }
}

// ── Focus trap selector ───────────────────────────────────────────────────────

const FOCUSABLE =
  'a[href]:not([tabindex="-1"]),' +
  'button:not([disabled]):not([tabindex="-1"]),' +
  'textarea:not([disabled]):not([tabindex="-1"]),' +
  'input:not([disabled]):not([tabindex="-1"]),' +
  'select:not([disabled]):not([tabindex="-1"]),' +
  '[tabindex]:not([tabindex="-1"])';

// ── Icons ─────────────────────────────────────────────────────────────────────

function XIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ModalProps {
  /** Whether the modal is currently shown */
  isOpen: boolean;
  /** Called when the user requests the modal to close */
  onClose: () => void;
  /** Heading rendered in the modal header */
  title?: string;
  /** Subheading rendered below the title */
  description?: string;
  /** Controls the maximum width of the panel */
  size?: "sm" | "md" | "lg" | "xl" | "full";
  /** Renders an × button in the header (default true) */
  showCloseButton?: boolean;
  /** Clicking the dark backdrop closes the modal (default true) */
  closeOnOverlayClick?: boolean;
  /** Content rendered in the sticky footer — typically action buttons */
  footer?: React.ReactNode;
  children: React.ReactNode;
}

// ── Size map ──────────────────────────────────────────────────────────────────

const PANEL_WIDTH = {
  sm:   "w-full max-w-[400px]",
  md:   "w-full max-w-[560px]",
  lg:   "w-full max-w-[720px]",
  xl:   "w-full max-w-[900px]",
  full: "w-full max-w-none",
};

// ── Component ─────────────────────────────────────────────────────────────────

export function Modal({
  isOpen,
  onClose,
  title,
  description,
  size = "md",
  showCloseButton = true,
  closeOnOverlayClick = true,
  footer,
  children,
}: ModalProps) {
  const id = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  // `mounted` keeps the DOM node alive during the leave animation.
  // `visible` drives the CSS transition (opacity / scale).
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      lockBodyScroll();
      // Trigger enter animation on next paint
      const frame = requestAnimationFrame(() => setVisible(true));
      return () => {
        cancelAnimationFrame(frame);
        unlockBodyScroll();
      };
    } else {
      setVisible(false);
      const t = setTimeout(() => setMounted(false), 200);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  // Focus trap + Escape
  useEffect(() => {
    if (!visible || !panelRef.current) return;

    const els = () =>
      Array.from(panelRef.current!.querySelectorAll<HTMLElement>(FOCUSABLE));

    // Auto-focus first focusable element on open
    els()[0]?.focus();

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const focusable = els();
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [visible, onClose]);

  if (!mounted || typeof document === "undefined") return null;

  const isFull = size === "full";

  return createPortal(
    <div
      className={cn(
        "fixed inset-0 z-[1040] flex",
        isFull ? "" : "items-center justify-center p-4",
      )}
      role="presentation"
    >
      {/* Backdrop */}
      <div
        aria-hidden="true"
        className={cn(
          "absolute inset-0 bg-black/50 backdrop-blur-sm",
          "transition-opacity duration-200",
          visible ? "opacity-100" : "opacity-0",
        )}
        onClick={closeOnOverlayClick ? onClose : undefined}
      />

      {/* Panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? `${id}-title` : undefined}
        aria-describedby={description ? `${id}-desc` : undefined}
        tabIndex={-1}
        className={cn(
          "relative z-[1050] flex flex-col outline-none",
          "bg-surface dark:bg-surface-raised",
          "transition-all duration-200",
          visible ? "opacity-100 scale-100" : "opacity-0 scale-95",
          isFull
            ? "w-full h-full rounded-none"
            : cn(
                "rounded-card max-h-[calc(100vh-2rem)]",
                PANEL_WIDTH[size],
              ),
        )}
        style={{ boxShadow: "var(--shadow-modal)" }}
      >
        {/* Header */}
        {(title || description || showCloseButton) && (
          <div className="flex items-start gap-4 shrink-0 px-6 py-4 border-b border-border-default dark:border-border-strong">
            <div className="flex-1 min-w-0">
              {title && (
                <h2
                  id={`${id}-title`}
                  className="text-xl font-semibold text-content-primary"
                >
                  {title}
                </h2>
              )}
              {description && (
                <p
                  id={`${id}-desc`}
                  className="mt-1 text-sm text-content-secondary"
                >
                  {description}
                </p>
              )}
            </div>
            {showCloseButton && (
              <button
                type="button"
                aria-label="Close modal"
                onClick={onClose}
                className={cn(
                  "shrink-0 p-1.5 rounded-md transition-colors duration-150",
                  "text-content-tertiary hover:text-content-primary",
                  "hover:bg-surface-overlay dark:hover:bg-surface-overlay",
                  "focus-visible:outline-none focus-visible:ring-2",
                  "focus-visible:ring-brand-purple focus-visible:ring-offset-2",
                  "focus-visible:ring-offset-surface",
                )}
              >
                <XIcon />
              </button>
            )}
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="shrink-0 flex items-center justify-end gap-3 px-6 py-4 border-t border-border-default dark:border-border-strong">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

Modal.displayName = "Modal";
